var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { addAxes, addGridLines, addLegend, areaPathFromPoints, dataBounds, seriesToPoints, wireBarChartInteraction, wireChartInteraction, wireMultiSeriesChartInteraction, wireStackedBarChartInteraction, wireStackedHorizontalBarChartInteraction, } from '../../chartPrimitives';
import { createWidgetGroup, setParts, setState } from '../../helpers';
import { getActiveDashboard } from '../../theme';
import { resolveValueColor } from '../../colorStops';
import { attachIndexYHover } from '../core/interaction';
import { attachPlotWheelZoom } from '../core/zoom';
import { installChartRebuild } from '../core/refresh';
import { buildChartContext } from '../core/layout';
import { normalizeBumpRanks, parseSeries, stackSeries, detectUserSeriesColors, seriesForWidgetState } from '../core/series';
import { bandWidth, linearScale } from '../core/scales';
import { catmullRomPath, pointsToPairs, stepPoints } from '../core/spline';
function valueToY(v, layout, bounds) {
    var range = bounds.max - bounds.min || 1;
    return layout.plotY + layout.plotHeight - ((v - bounds.min) / range) * layout.plotHeight;
}
function addPlotChrome(app, group, ctx, props) {
    var width = ctx.width, height = ctx.height, layout = ctx.layout, bounds = ctx.bounds, yTicks = ctx.yTicks;
    var minimal = ctx.series.length === 0 || props.minimalAxes === true || ['sparkline'].includes(String(props.variant));
    group.add(app.rect({ width: width, height: height, fill: getActiveDashboard().chartBg, listening: true }));
    group.add(app.rect({
        x: layout.plotX,
        y: layout.plotY,
        width: layout.plotWidth,
        height: layout.plotHeight,
        fill: getActiveDashboard().chartPlot,
        stroke: null,
        listening: false,
    }));
    if (!minimal && props.showGrid !== false) {
        addGridLines(app, group, layout, yTicks, bounds);
        addAxes(app, group, layout, bounds, yTicks);
    }
}
function addInteraction(app, group, ctx, props, primaryData, allSeries) {
    if (props.interactive === false)
        return;
    var layout = ctx.layout, bounds = ctx.bounds;
    var crosshair = app.line({
        x: layout.plotX,
        y: layout.plotY,
        x2: 0,
        y2: layout.plotHeight,
        stroke: getActiveDashboard().chartCrosshair,
        strokeWidth: 1,
        dash: [4, 4],
        visible: false,
        listening: false,
    });
    var dot = app.circle({
        x: 0,
        y: 0,
        radius: 5,
        fill: getActiveDashboard().chartDot,
        stroke: getActiveDashboard().chartLine,
        strokeWidth: 2,
        visible: false,
        listening: false,
    });
    var tooltip = app.roundedRect({
        width: 52,
        height: 24,
        cornerRadius: 6,
        fill: getActiveDashboard().chartTooltipBg,
        stroke: getActiveDashboard().chartTooltipBorder,
        strokeWidth: 1,
        visible: false,
        listening: false,
        zIndex: 900,
    });
    var tooltipLabel = app.text({
        text: '',
        fontSize: 11,
        fontWeight: 'bold',
        fill: getActiveDashboard().text,
        textAlign: 'center',
        x: 0,
        y: 0,
        visible: false,
        listening: false,
        zIndex: 901,
    });
    var hitArea = app.rect({
        x: layout.plotX,
        y: layout.plotY,
        width: layout.plotWidth,
        height: layout.plotHeight,
        fill: 'rgba(0,0,0,0.001)',
        listening: true,
        zIndex: 800,
    });
    group.add(crosshair, dot, tooltip, tooltipLabel, hitArea);
    var seriesForHover = allSeries && allSeries.length > 1 ? allSeries : null;
    if (seriesForHover) {
        wireMultiSeriesChartInteraction(group, seriesForHover, layout, bounds, {
            tooltip: tooltip,
            tooltipLabel: tooltipLabel,
            crosshair: crosshair,
            dot: dot,
            hitArea: hitArea,
        });
    }
    else {
        wireChartInteraction(group, primaryData, layout, bounds, {
            tooltip: tooltip,
            tooltipLabel: tooltipLabel,
            crosshair: crosshair,
            dot: dot,
            hitArea: hitArea,
        });
    }
    if (props.zoomEnabled !== false && props.interactive !== false) {
        attachPlotWheelZoom(group, hitArea, bounds);
    }
    setParts(group, { crosshair: crosshair, dot: dot, tooltip: tooltip, tooltipLabel: tooltipLabel, hitArea: hitArea });
}
function drawLineSeries(app, group, ctx, series, variant) {
    var _a;
    var layout = ctx.layout, bounds = ctx.bounds;
    var color = (_a = series.color) !== null && _a !== void 0 ? _a : getActiveDashboard().chartLine;
    var toXY = function (i, v) { return [
        layout.plotX + (layout.plotWidth / Math.max(series.data.length - 1, 1)) * i,
        valueToY(v, layout, bounds),
    ]; };
    var points;
    if (variant === 'step' || variant === 'run' || variant === 'control') {
        points = stepPoints(series.data, toXY);
    }
    else {
        points = seriesToPoints(series.data, layout, bounds);
    }
    if (variant === 'area' || variant === 'stackedArea' || variant === 'rangeArea' || variant === 'ribbon' || variant === 'horizon') {
        var baselineY = layout.plotY + layout.plotHeight;
        group.add(app.path({
            d: areaPathFromPoints(points, baselineY),
            fill: color,
            opacity: 0.35,
            stroke: null,
            listening: false,
        }));
    }
    if (variant === 'spline') {
        var path = catmullRomPath(pointsToPairs(points));
        group.add(app.path({ d: path, fill: null, stroke: color, strokeWidth: 2.5, listening: false }));
    }
    else {
        group.add(app.polyline({
            points: points,
            fill: null,
            stroke: color,
            strokeWidth: 2.5,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
        }));
    }
}
function drawBarSeries(app, group, ctx, series, horizontal, stackBase, cluster) {
    var _a, _b, _c;
    var layout = ctx.layout, bounds = ctx.bounds;
    var n = series.data.length;
    var gap = 0.2;
    var bars = [];
    var fallback = (_a = series.color) !== null && _a !== void 0 ? _a : getActiveDashboard().barFill;
    var barFill = function (val) {
        var _a;
        return ((_a = series.colorStops) === null || _a === void 0 ? void 0 : _a.length)
            ? resolveValueColor(val, series.colorStops, fallback)
            : fallback;
    };
    var seriesCount = Math.max(1, (_b = cluster === null || cluster === void 0 ? void 0 : cluster.seriesCount) !== null && _b !== void 0 ? _b : 1);
    var seriesIndex = (_c = cluster === null || cluster === void 0 ? void 0 : cluster.seriesIndex) !== null && _c !== void 0 ? _c : 0;
    var clustered = seriesCount > 1 && !stackBase;
    if (horizontal) {
        var rowStep_1 = layout.plotHeight / Math.max(n, 1);
        var usable_1 = rowStep_1 * (1 - gap);
        var barH_1 = clustered ? Math.max(2, (usable_1 / seriesCount) * 0.88) : usable_1;
        var scale_1 = linearScale([bounds.min, bounds.max], [0, layout.plotWidth]);
        series.data.forEach(function (val, i) {
            var _a;
            var base = (_a = stackBase === null || stackBase === void 0 ? void 0 : stackBase[i]) !== null && _a !== void 0 ? _a : bounds.min;
            var x0 = scale_1(base);
            var x1 = scale_1(val);
            var clusterTop = layout.plotY + rowStep_1 * i + (rowStep_1 - usable_1) / 2;
            var y = clustered
                ? clusterTop + seriesIndex * (usable_1 / seriesCount) + ((usable_1 / seriesCount) - barH_1) / 2
                : clusterTop;
            var bar = app.rect({
                x: layout.plotX + x0,
                y: y,
                width: Math.max(1, x1 - x0),
                height: barH_1,
                fill: barFill(val),
                listening: false,
            });
            bars.push(bar);
            group.add(bar);
        });
    }
    else {
        var colStep_1 = layout.plotWidth / Math.max(n, 1);
        var usable_2 = colStep_1 * (1 - gap);
        var barW_1 = clustered ? Math.max(2, (usable_2 / seriesCount) * 0.88) : usable_2;
        series.data.forEach(function (val, i) {
            var _a;
            var base = (_a = stackBase === null || stackBase === void 0 ? void 0 : stackBase[i]) !== null && _a !== void 0 ? _a : bounds.min;
            var yTop = valueToY(val, layout, bounds);
            var yBase = valueToY(base, layout, bounds);
            var clusterLeft = layout.plotX + colStep_1 * i + (colStep_1 - usable_2) / 2;
            var x = clustered
                ? clusterLeft + seriesIndex * (usable_2 / seriesCount) + ((usable_2 / seriesCount) - barW_1) / 2
                : clusterLeft;
            var bar = app.rect({
                x: x,
                y: Math.min(yTop, yBase),
                width: barW_1,
                height: Math.max(1, Math.abs(yBase - yTop)),
                fill: barFill(val),
                listening: false,
            });
            bars.push(bar);
            group.add(bar);
        });
    }
    return bars;
}
function drawWaterfall(app, group, ctx, data) {
    var layout = ctx.layout, bounds = ctx.bounds;
    var n = data.length;
    var bw = bandWidth(n, layout.plotWidth, 0.25);
    var running = 0;
    data.forEach(function (delta, i) {
        var start = running;
        running += delta;
        var y0 = valueToY(start, layout, bounds);
        var y1 = valueToY(running, layout, bounds);
        var x = layout.plotX + (layout.plotWidth / n) * i + (layout.plotWidth / n - bw) / 2;
        group.add(app.rect({
            x: x,
            y: Math.min(y0, y1),
            width: bw,
            height: Math.max(1, Math.abs(y1 - y0)),
            fill: delta >= 0 ? getActiveDashboard().success : getActiveDashboard().danger,
            listening: false,
        }));
    });
}
function drawPareto(app, group, ctx, data) {
    var e_1, _a;
    drawBarSeries(app, group, ctx, { data: data, color: getActiveDashboard().barFill }, false);
    var sorted = __spreadArray([], __read(data), false);
    var total = sorted.reduce(function (a, b) { return a + b; }, 0) || 1;
    var cum = [];
    var acc = 0;
    try {
        for (var sorted_1 = __values(sorted), sorted_1_1 = sorted_1.next(); !sorted_1_1.done; sorted_1_1 = sorted_1.next()) {
            var v = sorted_1_1.value;
            acc += v;
            cum.push((acc / total) * 100);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (sorted_1_1 && !sorted_1_1.done && (_a = sorted_1.return)) _a.call(sorted_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var cumBounds = { min: 0, max: 100 };
    var pts = seriesToPoints(cum, ctx.layout, cumBounds);
    group.add(app.polyline({
        points: pts,
        fill: null,
        stroke: getActiveDashboard().warning,
        strokeWidth: 2,
        listening: false,
    }));
}
function drawControlChart(app, group, ctx, data, limits) {
    var e_2, _a;
    drawLineSeries(app, group, ctx, { data: data, color: getActiveDashboard().chartLine }, 'step');
    if (!limits) {
        var mean_1 = data.reduce(function (a, b) { return a + b; }, 0) / (data.length || 1);
        var sd = Math.sqrt(data.reduce(function (a, b) { return a + Math.pow((b - mean_1), 2); }, 0) / (data.length || 1));
        limits = { mean: mean_1, ucl: mean_1 + 2 * sd, lcl: mean_1 - 2 * sd };
    }
    try {
        for (var _b = __values([
            [limits.mean, 'μ', getActiveDashboard().textMuted],
            [limits.ucl, 'UCL', getActiveDashboard().danger],
            [limits.lcl, 'LCL', getActiveDashboard().danger],
        ]), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 3), val = _d[0], label = _d[1], color = _d[2];
            var y = valueToY(val, ctx.layout, ctx.bounds);
            group.add(app.line({
                x: ctx.layout.plotX,
                y: y,
                x2: ctx.layout.plotWidth,
                y2: 0,
                stroke: color,
                strokeWidth: 1,
                dash: label === 'μ' ? [] : [6, 4],
                listening: false,
            }), app.text({
                text: label,
                x: ctx.layout.plotX + ctx.layout.plotWidth - 24,
                y: y - 12,
                fontSize: 9,
                fill: color,
                listening: false,
            }));
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_2) throw e_2.error; }
    }
}
function drawPopulationPyramid(app, group, ctx, left, right) {
    var _a, _b;
    var max = Math.max.apply(Math, __spreadArray(__spreadArray(__spreadArray([], __read(left), false), __read(right), false), [1], false));
    var bounds = { min: -max, max: max };
    var n = Math.max(left.length, right.length);
    var bh = ctx.layout.plotHeight / n - 2;
    for (var i = 0; i < n; i++) {
        var y = ctx.layout.plotY + i * (bh + 2);
        var cx = ctx.layout.plotX + ctx.layout.plotWidth / 2;
        var lw = (((_a = left[i]) !== null && _a !== void 0 ? _a : 0) / max) * (ctx.layout.plotWidth / 2 - 4);
        var rw = (((_b = right[i]) !== null && _b !== void 0 ? _b : 0) / max) * (ctx.layout.plotWidth / 2 - 4);
        group.add(app.rect({ x: cx - lw, y: y, width: lw, height: bh, fill: getActiveDashboard().primary, listening: false }), app.rect({ x: cx, y: y, width: rw, height: bh, fill: getActiveDashboard().secondary, listening: false }));
    }
    ctx.bounds.min = bounds.min;
    ctx.bounds.max = bounds.max;
}
function drawLollipop(app, group, ctx, data) {
    var layout = ctx.layout, bounds = ctx.bounds;
    var n = data.length;
    data.forEach(function (val, i) {
        var x = layout.plotX + (layout.plotWidth / Math.max(n - 1, 1)) * i;
        var y = valueToY(val, layout, bounds);
        var y0 = layout.plotY + layout.plotHeight;
        group.add(app.line({ x: x, y: y0, x2: 0, y2: y - y0, stroke: getActiveDashboard().chartLine, strokeWidth: 2, listening: false }), app.circle({ x: x - 4, y: y - 4, radius: 4, fill: getActiveDashboard().chartDot, listening: false }));
    });
}
function drawDotStrip(app, group, ctx, data, strip) {
    if (strip === void 0) { strip = false; }
    var layout = ctx.layout, bounds = ctx.bounds;
    data.forEach(function (val, i) {
        var jitter = strip ? ((i % 5) - 2) * 4 : (Math.sin(i * 12.9898) * 43758.5453 % 1) * 10 - 5;
        var x = layout.plotX + (layout.plotWidth / Math.max(data.length, 1)) * (i + 0.5) + jitter;
        var y = valueToY(val, layout, bounds);
        group.add(app.circle({ x: x - 3, y: y - 3, radius: 3, fill: getActiveDashboard().chartDot, listening: false }));
    });
}
function drawErrorBars(app, group, ctx, series) {
    var _a;
    var layout = ctx.layout, bounds = ctx.bounds;
    var errors = (_a = series.errorY) !== null && _a !== void 0 ? _a : series.data.map(function (v) { return [v - 5, v + 5]; });
    series.data.forEach(function (val, i) {
        var _a;
        var x = layout.plotX + (layout.plotWidth / Math.max(series.data.length - 1, 1)) * i;
        var _b = __read((_a = errors[i]) !== null && _a !== void 0 ? _a : [val - 5, val + 5], 2), lo = _b[0], hi = _b[1];
        var yLo = valueToY(lo, layout, bounds);
        var yHi = valueToY(hi, layout, bounds);
        group.add(app.line({ x: x, y: yLo, x2: 0, y2: yHi - yLo, stroke: getActiveDashboard().textMuted, strokeWidth: 1, listening: false }), app.line({ x: x - 4, y: yLo, x2: 8, y2: 0, stroke: getActiveDashboard().textMuted, strokeWidth: 1, listening: false }), app.line({ x: x - 4, y: yHi, x2: 8, y2: 0, stroke: getActiveDashboard().textMuted, strokeWidth: 1, listening: false }), app.circle({ x: x - 3, y: valueToY(val, layout, bounds) - 3, radius: 3, fill: getActiveDashboard().chartLine, listening: false }));
    });
}
function drawRangeBand(app, group, ctx, series, filled) {
    var _a, _b;
    var mins = (_a = series.rangeMin) !== null && _a !== void 0 ? _a : series.data.map(function (v) { return v - 8; });
    var maxs = (_b = series.rangeMax) !== null && _b !== void 0 ? _b : series.data.map(function (v) { return v + 8; });
    var layout = ctx.layout, bounds = ctx.bounds;
    var ptsTop = [];
    var ptsBot = [];
    for (var i = 0; i < series.data.length; i++) {
        var x = layout.plotX + (layout.plotWidth / Math.max(series.data.length - 1, 1)) * i;
        ptsTop.push(x, valueToY(maxs[i], layout, bounds));
        ptsBot.unshift(valueToY(mins[i], layout, bounds), x);
    }
    var all = __spreadArray(__spreadArray([], __read(ptsTop), false), __read(ptsBot), false);
    if (filled) {
        var d = "M ".concat(all[0], " ").concat(all[1]);
        for (var i = 2; i < all.length; i += 2)
            d += " L ".concat(all[i], " ").concat(all[i + 1]);
        d += ' Z';
        group.add(app.path({ d: d, fill: getActiveDashboard().chartArea, stroke: null, listening: false }));
    }
    else {
        group.add(app.polyline({ points: ptsTop, fill: null, stroke: getActiveDashboard().chartLine, strokeWidth: 1.5, listening: false }), app.polyline({ points: ptsBot.reverse(), fill: null, stroke: getActiveDashboard().chartLine, strokeWidth: 1.5, listening: false }));
    }
    drawLineSeries(app, group, ctx, series, 'line');
}
function drawHorizon(app, group, ctx, series, bands, rowLayout) {
    var _a;
    if (bands === void 0) { bands = 4; }
    var layout = rowLayout !== null && rowLayout !== void 0 ? rowLayout : ctx.layout;
    var bounds = ctx.bounds;
    var bandH = layout.plotHeight / bands;
    var offset = (bounds.max - bounds.min) / bands;
    for (var b = 0; b < bands; b++) {
        var subBounds = { min: bounds.min + b * offset, max: bounds.min + (b + 1) * offset };
        var pts = seriesToPoints(series.data, __assign(__assign({}, layout), { plotY: layout.plotY + b * bandH, plotHeight: bandH }), subBounds);
        group.add(app.path({
            d: areaPathFromPoints(pts, layout.plotY + (b + 1) * bandH),
            fill: (_a = series.color) !== null && _a !== void 0 ? _a : getActiveDashboard().series[b % getActiveDashboard().series.length],
            opacity: 0.55,
            stroke: null,
            listening: false,
        }));
    }
}
function drawHorizonRows(app, group, ctx, allSeries) {
    var rowH = ctx.layout.plotHeight / allSeries.length;
    allSeries.forEach(function (s, si) {
        var _a;
        var rowLayout = __assign(__assign({}, ctx.layout), { plotY: ctx.layout.plotY + si * rowH, plotHeight: Math.max(8, rowH - 2) });
        var rowBounds = dataBounds(s.data);
        drawHorizon(app, group, __assign(__assign({}, ctx), { bounds: rowBounds }), s, 3, rowLayout);
        group.add(app.text({
            text: (_a = s.name) !== null && _a !== void 0 ? _a : "S".concat(si + 1),
            x: ctx.layout.plotX - 2,
            y: rowLayout.plotY + 2,
            fontSize: 9,
            fill: getActiveDashboard().textMuted,
            listening: false,
        }));
    });
}
export function buildCartesianChart(group, app, props, options) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
    var variant = options.variant;
    var seriesHasUserColors = detectUserSeriesColors(props);
    var series = parseSeries(props, [10, 30, 20, 50, 40, 60], { keepColors: seriesHasUserColors });
    var rawSeries = series.map(function (s) { return ({
        name: s.name,
        data: __spreadArray([], __read(s.data), false),
        color: s.color,
    }); });
    var mirror = props.mirrorData;
    if (variant === 'stackedBar' || variant === 'stackedColumn' || variant === 'stackedArea') {
        series = stackSeries(series);
    }
    if (variant === 'bump') {
        series = normalizeBumpRanks(series);
    }
    var ctx = buildChartContext(props, series);
    addPlotChrome(app, group, ctx, __assign(__assign({}, props), { variant: variant }));
    var horizontal = variant === 'horizontalBar' ||
        variant === 'stackedBar' ||
        variant === 'populationPyramid' ||
        props.orientation === 'horizontal';
    if (variant === 'waterfall') {
        drawWaterfall(app, group, ctx, (_b = (_a = series[0]) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : []);
    }
    else if (variant === 'pareto') {
        drawPareto(app, group, ctx, (_d = (_c = series[0]) === null || _c === void 0 ? void 0 : _c.data) !== null && _d !== void 0 ? _d : []);
    }
    else if (variant === 'control') {
        drawControlChart(app, group, ctx, (_f = (_e = series[0]) === null || _e === void 0 ? void 0 : _e.data) !== null && _f !== void 0 ? _f : [], props.controlLimits);
    }
    else if (variant === 'populationPyramid') {
        drawPopulationPyramid(app, group, ctx, (_h = (_g = series[0]) === null || _g === void 0 ? void 0 : _g.data) !== null && _h !== void 0 ? _h : [], (_k = mirror !== null && mirror !== void 0 ? mirror : (_j = series[1]) === null || _j === void 0 ? void 0 : _j.data) !== null && _k !== void 0 ? _k : []);
    }
    else if (variant === 'lollipop') {
        drawLollipop(app, group, ctx, (_m = (_l = series[0]) === null || _l === void 0 ? void 0 : _l.data) !== null && _m !== void 0 ? _m : []);
    }
    else if (variant === 'dotPlot') {
        drawDotStrip(app, group, ctx, (_p = (_o = series[0]) === null || _o === void 0 ? void 0 : _o.data) !== null && _p !== void 0 ? _p : [], false);
    }
    else if (variant === 'stripPlot') {
        drawDotStrip(app, group, ctx, (_r = (_q = series[0]) === null || _q === void 0 ? void 0 : _q.data) !== null && _r !== void 0 ? _r : [], true);
    }
    else if (variant === 'errorBar') {
        drawErrorBars(app, group, ctx, series[0]);
    }
    else if (variant === 'range' || variant === 'band') {
        drawRangeBand(app, group, ctx, series[0], false);
    }
    else if (variant === 'rangeArea') {
        drawRangeBand(app, group, ctx, series[0], true);
    }
    else if (variant === 'horizon') {
        if (series.length > 1) {
            drawHorizonRows(app, group, ctx, series);
        }
        else {
            drawHorizon(app, group, ctx, series[0]);
        }
    }
    else if (variant === 'bar' ||
        variant === 'horizontalBar' ||
        variant === 'stackedBar' ||
        variant === 'stackedColumn' ||
        variant === 'combination' ||
        variant === 'mixed') {
        var stacked_1 = variant === 'stackedBar' || variant === 'stackedColumn';
        var clusterBars_1 = !stacked_1 &&
            series.length > 1 &&
            (variant === 'bar' || variant === 'horizontalBar');
        series.forEach(function (s, si) {
            var _a;
            var base = stacked_1 ? s._base : undefined;
            var kind = variant === 'mixed' || variant === 'combination' ? (_a = s.type) !== null && _a !== void 0 ? _a : (si === 0 ? 'bar' : 'line') : 'bar';
            if (kind === 'line' || kind === 'area') {
                drawLineSeries(app, group, ctx, s, kind === 'area' ? 'area' : 'line');
            }
            else {
                drawBarSeries(app, group, ctx, s, horizontal, base, clusterBars_1 ? { seriesIndex: si, seriesCount: series.length } : undefined);
            }
        });
    }
    else if (variant === 'ribbon') {
        series.forEach(function (s) { return drawLineSeries(app, group, ctx, s, 'area'); });
    }
    else {
        series.forEach(function (s) {
            drawLineSeries(app, group, ctx, s, variant === 'stackedArea' ? 'stackedArea' : variant);
        });
    }
    if (props.showLegend !== false && variant !== 'sparkline') {
        addLegend(app, group, series.map(function (s) { var _a, _b; return ({ label: (_a = s.name) !== null && _a !== void 0 ? _a : 'Series', color: (_b = s.color) !== null && _b !== void 0 ? _b : getActiveDashboard().chartLine }); }), ctx.layout.plotX, ctx.layout.plotY + ctx.layout.plotHeight + 4);
    }
    var primaryData = (_t = (_s = series[0]) === null || _s === void 0 ? void 0 : _s.data) !== null && _t !== void 0 ? _t : [];
    var isStacked = variant === 'stackedBar' || variant === 'stackedColumn';
    var multiBar = rawSeries.length > 1 && (variant === 'bar' || variant === 'horizontalBar' || isStacked);
    var stackedMulti = isStacked && rawSeries.length > 1;
    var categoryMax = multiBar
        ? rawSeries[0].data.map(function (_, i) { return Math.max.apply(Math, __spreadArray(__spreadArray([], __read(rawSeries.map(function (s) { var _a; return (_a = s.data[i]) !== null && _a !== void 0 ? _a : 0; })), false), [0], false)); })
        : primaryData;
    var stackedTotals = isStacked ? ((_v = (_u = series[series.length - 1]) === null || _u === void 0 ? void 0 : _u.data) !== null && _v !== void 0 ? _v : primaryData) : categoryMax;
    var hoverTotals = stackedMulti ? stackedTotals : categoryMax;
    if (['bar', 'horizontalBar', 'stackedBar', 'stackedColumn', 'waterfall', 'pareto'].includes(variant)) {
        if (horizontal) {
            var n_1 = primaryData.length;
            if (multiBar) {
                var highlight = app.rect({
                    fill: 'rgba(96,165,250,0.28)',
                    stroke: getActiveDashboard().chartLine,
                    strokeWidth: 2,
                    visible: false,
                    listening: false,
                });
                var tooltip = app.roundedRect({
                    width: 52,
                    height: 24,
                    cornerRadius: 6,
                    fill: getActiveDashboard().chartTooltipBg,
                    stroke: getActiveDashboard().chartTooltipBorder,
                    strokeWidth: 1,
                    visible: false,
                    listening: false,
                });
                var tooltipLabel = app.text({
                    text: '',
                    fontSize: 11,
                    fontWeight: 'bold',
                    fill: getActiveDashboard().text,
                    textAlign: 'center',
                    x: 0,
                    y: 0,
                    listening: false,
                });
                var hitArea = app.rect({
                    x: ctx.layout.plotX,
                    y: ctx.layout.plotY,
                    width: ctx.layout.plotWidth,
                    height: ctx.layout.plotHeight,
                    fill: 'rgba(0,0,0,0.001)',
                    listening: true,
                });
                group.add(highlight, tooltip, tooltipLabel, hitArea);
                if (props.interactive !== false) {
                    wireStackedHorizontalBarChartInteraction(group, rawSeries, ctx.layout, ctx.bounds, hoverTotals, 0.2, {
                        tooltip: tooltip,
                        tooltipLabel: tooltipLabel,
                        highlight: highlight,
                        hitArea: hitArea,
                    });
                }
                setParts(group, { highlight: highlight, tooltip: tooltip, tooltipLabel: tooltipLabel, hitArea: hitArea });
            }
            else {
                var bw_1 = bandWidth(n_1, ctx.layout.plotHeight, 0.2);
                var xScale_1 = linearScale([ctx.bounds.min, ctx.bounds.max], [0, ctx.layout.plotWidth]);
                attachIndexYHover(app, group, props, ctx.layout, n_1, function (i) { return String(primaryData[i]); }, function (i) {
                    var slot = ctx.layout.plotHeight / Math.max(n_1, 1);
                    var val = primaryData[i];
                    var x0 = ctx.layout.plotX + xScale_1(ctx.bounds.min);
                    var x1 = ctx.layout.plotX + xScale_1(val);
                    var y = ctx.layout.plotY + slot * i + (slot - bw_1) / 2;
                    return { x: x0, y: y, width: Math.max(1, x1 - x0), height: bw_1 };
                });
            }
        }
        else {
            var highlight = app.rect({ fill: 'rgba(96,165,250,0.28)', stroke: getActiveDashboard().chartLine, strokeWidth: 2, visible: false, listening: false });
            var tooltip = app.roundedRect({ width: 52, height: 24, cornerRadius: 6, fill: getActiveDashboard().chartTooltipBg, stroke: getActiveDashboard().chartTooltipBorder, strokeWidth: 1, visible: false, listening: false });
            var tooltipLabel = app.text({ text: '', fontSize: 11, fontWeight: 'bold', fill: getActiveDashboard().text, textAlign: 'center', x: 0, y: 0, listening: false });
            var hitArea = app.rect({ x: ctx.layout.plotX, y: ctx.layout.plotY, width: ctx.layout.plotWidth, height: ctx.layout.plotHeight, fill: 'rgba(0,0,0,0.001)', listening: true });
            group.add(highlight, tooltip, tooltipLabel, hitArea);
            if (props.interactive !== false) {
                if (multiBar) {
                    wireStackedBarChartInteraction(group, rawSeries, ctx.layout, ctx.bounds, hoverTotals, 0.2, {
                        tooltip: tooltip,
                        tooltipLabel: tooltipLabel,
                        highlight: highlight,
                        hitArea: hitArea,
                    });
                }
                else {
                    wireBarChartInteraction(group, primaryData, ctx.layout, ctx.bounds, 0.2, {
                        tooltip: tooltip,
                        tooltipLabel: tooltipLabel,
                        highlight: highlight,
                        hitArea: hitArea,
                    });
                }
            }
            setParts(group, { highlight: highlight, tooltip: tooltip, tooltipLabel: tooltipLabel, hitArea: hitArea });
        }
    }
    else if (variant !== 'sparkline') {
        addInteraction(app, group, ctx, props, primaryData, series.length > 1 ? series : undefined);
    }
    setState(group, __assign(__assign({ width: ctx.width, height: ctx.height, data: primaryData, series: (_w = seriesForWidgetState(props, seriesHasUserColors)) !== null && _w !== void 0 ? _w : [], seriesHasUserColors: seriesHasUserColors, variant: variant }, (Array.isArray(props.colorStops) ? { colorStops: props.colorStops } : {})), (Array.isArray(props.thresholds) && !props.colorStops
        ? { thresholds: props.thresholds }
        : {})));
}
export function createCartesianWidget(app, type, props, variant) {
    var group = createWidgetGroup(app, type, props);
    installChartRebuild(group, app, function (g, a, p) {
        return buildCartesianChart(g, a, p, { variant: variant, widgetType: type });
    });
    return group;
}
